// src/pages/admin/AdminBanners.jsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiUpload, FiImage, FiArrowUp, FiArrowDown, FiGrip } from 'react-icons/fi';
import styles from './AdminBanners.module.css';

const AdminBanners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    images: [], // array of image URLs
    title: '',
    subtitle: '',
    link: '',
    is_active: true,
    display_order: 0,
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('banners')
      .select('*')
      .order('display_order', { ascending: true });
    if (error) {
      console.error('Error fetching banners:', error);
      setMessage({ type: 'error', text: 'Failed to load banners' });
    } else {
      // Ensure images is always an array
      const parsed = data.map(b => ({
        ...b,
        images: b.images || (b.image_url ? [b.image_url] : [])
      }));
      setBanners(parsed || []);
    }
    setLoading(false);
  };

  const uploadImage = async (file) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `banner_${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `banners/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('product-images')
      .upload(filePath, file);

    if (uploadError) {
      console.error('Upload error:', uploadError);
      return null;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('product-images')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please upload an image file' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image size should be less than 5MB' });
      return;
    }

    setUploading(true);
    const imageUrl = await uploadImage(file);
    if (imageUrl) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, imageUrl]
      }));
      setMessage({ type: 'success', text: 'Image uploaded successfully!' });
    } else {
      setMessage({ type: 'error', text: 'Failed to upload image' });
    }
    setUploading(false);
    setTimeout(() => setMessage(''), 3000);
    // Reset file input
    e.target.value = '';
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const moveImageUp = (index) => {
    if (index === 0) return;
    const newImages = [...formData.images];
    [newImages[index], newImages[index - 1]] = [newImages[index - 1], newImages[index]];
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const moveImageDown = (index) => {
    if (index === formData.images.length - 1) return;
    const newImages = [...formData.images];
    [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const handleOpenModal = (banner = null) => {
    if (banner) {
      setEditingBanner(banner);
      setFormData({
        images: banner.images || (banner.image_url ? [banner.image_url] : []),
        title: banner.title || '',
        subtitle: banner.subtitle || '',
        link: banner.link || '',
        is_active: banner.is_active,
        display_order: banner.display_order || 0,
      });
    } else {
      setEditingBanner(null);
      setFormData({
        images: [],
        title: '',
        subtitle: '',
        link: '',
        is_active: true,
        display_order: banners.length,
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.images.length === 0) {
      setMessage({ type: 'error', text: 'Please upload at least one image' });
      return;
    }

    setSaving(true);
    const data = {
      images: formData.images, // store as JSON array
      title: formData.title.trim(),
      subtitle: formData.subtitle.trim(),
      link: formData.link.trim(),
      is_active: formData.is_active,
      display_order: parseInt(formData.display_order) || 0,
      updated_at: new Date().toISOString(),
    };

    // Also set image_url as first image for backward compatibility
    data.image_url = formData.images[0] || '';

    let result;
    if (editingBanner) {
      result = await supabase
        .from('banners')
        .update(data)
        .eq('id', editingBanner.id);
    } else {
      result = await supabase
        .from('banners')
        .insert([{ ...data, created_at: new Date().toISOString() }]);
    }

    if (result.error) {
      console.error('Save error:', result.error);
      setMessage({ type: 'error', text: 'Failed to save banner' });
    } else {
      setMessage({ type: 'success', text: `Banner ${editingBanner ? 'updated' : 'added'} successfully!` });
      fetchBanners();
      setShowModal(false);
    }
    setSaving(false);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;
    const { error } = await supabase
      .from('banners')
      .delete()
      .eq('id', id);
    if (error) {
      setMessage({ type: 'error', text: 'Failed to delete banner' });
    } else {
      setMessage({ type: 'success', text: 'Banner deleted!' });
      fetchBanners();
    }
    setTimeout(() => setMessage(''), 3000);
  };

  const toggleActive = async (id, current) => {
    const { error } = await supabase
      .from('banners')
      .update({ is_active: !current, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (!error) fetchBanners();
  };

  const moveOrder = async (id, direction) => {
    const index = banners.findIndex(b => b.id === id);
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === banners.length - 1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const swapped = [...banners];
    [swapped[index], swapped[newIndex]] = [swapped[newIndex], swapped[index]];

    await supabase
      .from('banners')
      .update({ display_order: swapped[index].display_order })
      .eq('id', swapped[index].id);
    await supabase
      .from('banners')
      .update({ display_order: swapped[newIndex].display_order })
      .eq('id', swapped[newIndex].id);

    fetchBanners();
  };

  if (loading) {
    return <div className={styles.loadingContainer}><div className={styles.loadingSpinner}></div></div>;
  }

  return (
    <div className={styles.adminBanners}>
      {message && (
        <div className={`${styles.message} ${styles[message.type]}`}>
          {message.text}
        </div>
      )}

      <div className={styles.header}>
        <h2>Home Page Banners</h2>
        <button onClick={() => handleOpenModal()} className={styles.addBtn}>
          <FiPlus /> Add Banner
        </button>
      </div>

      <div className={styles.bannerList}>
        {banners.length === 0 ? (
          <div className={styles.emptyState}>
            <FiImage className={styles.emptyIcon} />
            <h3>No Banners Yet</h3>
            <p>Add banners to showcase on your home page.</p>
            <button onClick={() => handleOpenModal()} className={styles.emptyBtn}>
              <FiPlus /> Add First Banner
            </button>
          </div>
        ) : (
          <div className={styles.bannerGrid}>
            {banners.map((banner, index) => {
              const images = banner.images || (banner.image_url ? [banner.image_url] : []);
              return (
                <div key={banner.id} className={styles.bannerCard}>
                  <div className={styles.bannerImage}>
                    {images.length > 0 ? (
                      <img src={images[0]} alt={banner.title || 'Banner'} />
                    ) : (
                      <div className={styles.noImage}>No Image</div>
                    )}
                    {images.length > 1 && (
                      <span className={styles.imageCount}>+{images.length - 1} more</span>
                    )}
                    {!banner.is_active && (
                      <div className={styles.inactiveOverlay}>Inactive</div>
                    )}
                  </div>
                  <div className={styles.bannerContent}>
                    <div className={styles.bannerInfo}>
                      {banner.title && <h4>{banner.title}</h4>}
                      {banner.subtitle && <p>{banner.subtitle}</p>}
                      {banner.link && <a href={banner.link} target="_blank" rel="noopener noreferrer">Link</a>}
                    </div>
                    <div className={styles.bannerActions}>
                      <button onClick={() => toggleActive(banner.id, banner.is_active)} className={styles.toggleBtn}>
                        {banner.is_active ? 'Active' : 'Inactive'}
                      </button>
                      <button onClick={() => moveOrder(banner.id, 'up')} className={styles.moveBtn} disabled={index === 0}>
                        <FiArrowUp />
                      </button>
                      <button onClick={() => moveOrder(banner.id, 'down')} className={styles.moveBtn} disabled={index === banners.length - 1}>
                        <FiArrowDown />
                      </button>
                      <button onClick={() => handleOpenModal(banner)} className={styles.editBtn}>
                        <FiEdit2 />
                      </button>
                      <button onClick={() => handleDelete(banner.id)} className={styles.deleteBtn}>
                        <FiTrash2 />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && (
        <div className={styles.modal} onClick={() => setShowModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{editingBanner ? 'Edit Banner' : 'Add New Banner'}</h3>
              <button onClick={() => setShowModal(false)} className={styles.closeBtn}><FiX /></button>
            </div>
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label>Banner Images * (upload 1–4 images)</label>
                <div className={styles.imageUploadArea}>
                  <div className={styles.imageGrid}>
                    {formData.images.map((url, idx) => (
                      <div key={idx} className={styles.imagePreviewItem}>
                        <img src={url} alt={`Banner ${idx+1}`} />
                        <div className={styles.imagePreviewOverlay}>
                          <button type="button" onClick={() => moveImageUp(idx)} disabled={idx === 0}>
                            <FiArrowUp />
                          </button>
                          <button type="button" onClick={() => moveImageDown(idx)} disabled={idx === formData.images.length - 1}>
                            <FiArrowDown />
                          </button>
                          <button type="button" onClick={() => removeImage(idx)}>
                            <FiTrash2 />
                          </button>
                        </div>
                      </div>
                    ))}
                    {formData.images.length < 4 && (
                      <label className={styles.uploadLabel}>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploading}
                          style={{ display: 'none' }}
                        />
                        <div className={styles.uploadBox}>
                          {uploading ? (
                            <div className={styles.uploading}>Uploading...</div>
                          ) : (
                            <>
                              <FiUpload />
                              <span>Add Image</span>
                              <small>{formData.images.length}/4</small>
                            </>
                          )}
                        </div>
                      </label>
                    )}
                  </div>
                  <p className={styles.imageHint}>Recommended: 1920x600 (desktop), 768x400 (mobile). Up to 4 images.</p>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Title (optional)</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Summer Sale!"
                />
              </div>

              <div className={styles.formGroup}>
                <label>Subtitle (optional)</label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                  placeholder="e.g., Up to 50% off on selected items"
                />
              </div>

              <div className={styles.formGroup}>
                <label>Link URL (optional)</label>
                <input
                  type="url"
                  value={formData.link}
                  onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                  placeholder="https://etikoppakatoys.store/products"
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                    min="0"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                    Active
                  </label>
                </div>
              </div>

              <div className={styles.formActions}>
                <button type="button" onClick={() => setShowModal(false)} className={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" disabled={saving || uploading} className={styles.submitBtn}>
                  {saving ? 'Saving...' : (editingBanner ? 'Update Banner' : 'Add Banner')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBanners;